import { useState, useCallback, useEffect } from 'react'
import { Button, Notification } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'

import assemblyLineIllustration from '@/assets/EMMAi_illu_assembly_line.svg'
import DraggableLineSelection from './DraggableLineSelection'
import type { LineOption } from '@/utils/storage'

import './LineOnboarding.scss'

type RowItem = { id: string; value: string }

interface LineOnboardingProps {
    availableLines: LineOption[]
    spaceName: string
    onComplete: (preferredLines: LineOption[]) => void
    isSaving: boolean
    saveError: string | null
    onClearSaveError: () => void
}

const LineOnboarding = ({
    availableLines,
    spaceName,
    onComplete,
    isSaving,
    saveError,
    onClearSaveError,
}: LineOnboardingProps) => {
    const { t } = useTranslation()
    const [rows, setRows] = useState<RowItem[]>([{ id: 'row-0', value: '' }])

    const filledLines: LineOption[] = rows
        .map((row) => availableLines.find((line) => line.value === row.value))
        .filter(Boolean) as LineOption[]

    const canSubmit = filledLines.length > 0 && !isSaving

    useEffect(() => {
        if (saveError) onClearSaveError()
        // The error is cleared when the rows change, not when it appears (it would be cleared at once)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, onClearSaveError])

    const handleDone = useCallback(() => {
        if (!canSubmit) return
        onComplete(filledLines)
    }, [canSubmit, filledLines, onComplete])

    return (
        <div className="line-onboarding" role="main">
            <div className="line-onboarding__content">
                <div className="line-onboarding__welcome-wrapper">
                    <h1 className="line-onboarding__welcome-wrapper__title">
                        {t('onboarding:titlePrefix')} {spaceName} {t('onboarding:titleSuffix')}
                    </h1>
                    <img
                        className="line-onboarding__welcome-wrapper__illustration"
                        src={assemblyLineIllustration}
                        alt=""
                        aria-hidden="true"
                    />
                </div>
                <div className="line-onboarding__content-wrapper">
                    <p className="line-onboarding__content-wrapper__subtitle">{t('onboarding:subtitle')}</p>
                    <DraggableLineSelection
                        availableLines={availableLines}
                        rows={rows}
                        setRows={setRows}
                        labelPrefix={t('general:settings:lineLabel')}
                        placeholder={t('general:settings:linePlaceholder')}
                    />
                    <div className="line-onboarding__content-wrapper__done-wrapper">
                        <Button
                            mode="primary"
                            label={t('onboarding:done')}
                            disabled={!canSubmit}
                            onClick={handleDone}
                        />
                    </div>
                    <div className="line-onboarding__content-wrapper__notification">
                        {saveError && (
                            <Notification
                                type="error"
                                defaultOpen
                                className="line-onboarding__content-wrapper__error"
                            >
                                {t('onboarding:saveError')}
                            </Notification>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LineOnboarding
